// src/pages/conversations/ConversationDetail.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, CircularProgress, Button, Divider,
  useTheme, alpha, Accordion, AccordionSummary, AccordionDetails,
  styled, Grid, Paper as MuiPaper, Switch, FormControlLabel, ButtonGroup, Stack,
  IconButton, Tooltip // Added Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon, Info as InfoIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  ArrowLeft as PrevIcon, ArrowRight as NextIcon,
  MenuOpen as MenuOpenIcon, ViewSidebar as ViewSidebarIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { conversationService } from '../../api/conversationService';
import useMediaQuery from '@mui/material/useMediaQuery';

// Import message components
import UserMessage from './messageComponents/UserMessage';
import AssistantMessage from './messageComponents/AssistantMessage';
import ToolCallMessage from './messageComponents/ToolCallMessage';
import ToolReturnMessage from './messageComponents/ToolReturnMessage';
import PageContextDisplay from './messageComponents/PageContextDisplay';

// Import the new sidebar component
import ConversationSidebarList from './ConversationSidebarList';

const StyledSystemPromptPaper = styled(MuiPaper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent', 
}));

const ConversationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const messagesEndRef = useRef(null);

  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const [isSidebarOpen, setIsSidebarOpen] = useState(isSmUp);

  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processedMessages, setProcessedMessages] = useState([]);
  const [expandedPageContexts, setExpandedPageContexts] = useState({});
  const [systemPrompt, setSystemPrompt] = useState(null);
  const [includeInternalParts, setIncludeInternalParts] = useState(false);

  const [prevConvId, setPrevConvId] = useState(null);
  const [nextConvId, setNextConvId] = useState(null);
  const [neighborsLoading, setNeighborsLoading] = useState(false);
  const [currentListParams, setCurrentListParams] = useState(null);

  useEffect(() => {
    // Initialize sidebar open state based on screen size and if coming from a list
    const hasListContext = !!location.state?.listParams;
    setIsSidebarOpen(isSmUp && hasListContext); // Open on sm+ if there's context, else closed.
                                                 // User can always toggle.
  }, [isSmUp, location.state]);


  useEffect(() => {
    if (location.state?.listParams) {
      setCurrentListParams(location.state.listParams);
    } else {
      setCurrentListParams(null);
    }
  }, [location.state]);


  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const processMessages = useCallback((convData) => {
    if (!convData || !convData.messages) {
      setProcessedMessages([]);
      setSystemPrompt(null);
      return;
    }
    
    let messagesSource = convData.messages;
    if (typeof messagesSource === 'string') {
      try {
        messagesSource = JSON.parse(messagesSource);
      } catch (e) {
        console.error('Error parsing messages JSON:', e);
        toast.error('Could not parse conversation messages.');
        setProcessedMessages([]); setSystemPrompt(null); return;
      }
    }
    if (!Array.isArray(messagesSource)) {
      console.error('Messages data is not an array:', messagesSource);
      toast.error('Invalid conversation message format.');
      setProcessedMessages([]); setSystemPrompt(null); return;
    }

    const newProcessed = [];
    let currentSysPrompt = null;
    messagesSource.forEach((message, msgIdx) => {
      try {
        // Process 'request' kind messages
        if (message.kind === 'request' && Array.isArray(message.parts)) {
          message.parts.forEach((part, partIdx) => {
            const uniqueIdBase = `msg-${msgIdx}-part-${partIdx}-${newProcessed.length}`;
            if (part.part_kind === 'user-prompt') {
              let userMessageContent = part.content;
              let pageContextData = null;
              // If includeInternalParts is false, and it's SYSTEM GUIDANCE, skip.
              if (typeof userMessageContent === 'string' && userMessageContent.startsWith("SYSTEM GUIDANCE") && !includeInternalParts) {
                return; 
              }

              if (userMessageContent && typeof userMessageContent === 'string') {
                const pageContextJsonMatch = userMessageContent.match(/({"url":.*?}(?:,\s*{"url":.*?})*)/s);
                if (pageContextJsonMatch && pageContextJsonMatch[0].trim() !== "") {
                  try {
                    JSON.parse(`[${pageContextJsonMatch[0]}]`); // Validate JSON structure
                    pageContextData = pageContextJsonMatch[0];
                    userMessageContent = userMessageContent.replace(pageContextData, '').trim();
                  } catch (jsonError) { /* Not valid JSON, so don't strip it */ }
                }
                if (!pageContextData && userMessageContent.trim().toLowerCase().startsWith('page context:')) {
                  const userMarkerIdx = userMessageContent.toLowerCase().indexOf("user:");
                  if (userMarkerIdx > -1 && userMarkerIdx > 'page context:'.length) {
                      pageContextData = userMessageContent.substring(0, userMarkerIdx).trim();
                      userMessageContent = userMessageContent.substring(userMarkerIdx + "user:".length).trim();
                  } else { 
                      pageContextData = userMessageContent;
                      userMessageContent = ""; // Assume entire content was page context
                  }
                }
              }
              if (pageContextData) {
                newProcessed.push({
                  id: `pc-${uniqueIdBase}`, type: 'page-context', 
                  content: pageContextData, timestamp: part.timestamp || message.timestamp
                });
              }
              if (userMessageContent && String(userMessageContent).trim()) {
                newProcessed.push({
                  id: `user-${uniqueIdBase}`, type: 'user',
                  content: String(userMessageContent).trim(), timestamp: part.timestamp || message.timestamp
                });
              }
            } else if (part.part_kind === 'system-prompt') {
              currentSysPrompt = part.content;
            } else if (part.part_kind === 'tool-return') {
              newProcessed.push({
                id: `toolreturn-${uniqueIdBase}`, type: 'tool-return',
                toolName: part.tool_name, content: part.content, toolCallId: part.tool_call_id,
                timestamp: part.timestamp || message.timestamp
              });
            }
          });
        } 
        // Process 'response' kind messages
        else if (message.kind === 'response' && Array.isArray(message.parts)) {
          message.parts.forEach((part, partIdx) => {
            const uniqueIdBase = `msg-${msgIdx}-part-${partIdx}-${newProcessed.length}`;
            if (part.part_kind === 'text') {
              newProcessed.push({
                id: `assistant-${uniqueIdBase}`, type: 'assistant',
                content: part.content, model: message.model_name, timestamp: message.timestamp
              });
            } else if (part.part_kind === 'tool-call') {
              newProcessed.push({
                id: `toolcall-${uniqueIdBase}`, type: 'tool-call',
                toolName: part.tool_name, args: part.args, toolCallId: part.tool_call_id,
                timestamp: message.timestamp
              });
            }
          });
        }
      } catch (e) {
        console.error('Error processing individual message item:', e, message);
        toast.error(`Error displaying a message part (ID: ${message.id || msgIdx}).`);
      }
    });
    setProcessedMessages(newProcessed);
    setSystemPrompt(currentSysPrompt);
  }, [includeInternalParts]);

  const loadConversation = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setConversation(null); 
    setProcessedMessages([]);
    setSystemPrompt(null);
    try {
      const response = await conversationService.getConversationById(id, includeInternalParts);
      if (response && response.isError) {
        toast.error(response.message || 'Failed to load conversation details.');
      } else if (response) {
        setConversation(response);
        processMessages(response);
      } else {
        toast.error('Received an unexpected empty response while loading conversation.');
      }
    } catch (error) {
      toast.error('An unexpected error occurred while loading conversation details.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id, includeInternalParts, processMessages]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [processedMessages]);

  useEffect(() => {
    const fetchNeighbors = async () => {
      if (!currentListParams || !id) {
        setPrevConvId(null);
        setNextConvId(null);
        return;
      }
      setNeighborsLoading(true);
      setPrevConvId(null);
      setNextConvId(null);
      try {
        const { agentIdForFilter, rowsPerPage, sortBy, sortOrder, searchTerm } = currentListParams;
        let currentPageInContext = currentListParams.page;
        const requests = [];
        requests.push(conversationService.listConversations(agentIdForFilter, rowsPerPage, currentPageInContext * rowsPerPage, searchTerm, sortBy, sortOrder));
        if (currentPageInContext > 0) {
          requests.push(conversationService.listConversations(agentIdForFilter, rowsPerPage, (currentPageInContext - 1) * rowsPerPage, searchTerm, sortBy, sortOrder));
        }
        const totalKnownConversations = currentListParams.totalConversations || 0;
        if ((currentPageInContext + 1) * rowsPerPage < totalKnownConversations) {
             requests.push(conversationService.listConversations(agentIdForFilter, rowsPerPage, (currentPageInContext + 1) * rowsPerPage, searchTerm, sortBy, sortOrder));
        }
        const responses = await Promise.all(requests);
        let allFetchedItems = [];
        responses.forEach(res => {
            if (res && !res.isError && Array.isArray(res.items)) {
                allFetchedItems = allFetchedItems.concat(res.items);
            }
        });
        const uniqueItems = Array.from(new Map(allFetchedItems.map(item => [item.id, item])).values());
        uniqueItems.sort((a, b) => {
            const valA = a[sortBy];
            const valB = b[sortBy];
            if (sortOrder === 'desc') { return valB > valA ? 1 : (valB < valA ? -1 : 0); }
            return valA > valB ? 1 : (valA < valB ? -1 : 0);
        });
        const neighborIds = uniqueItems.map(c => c.id);
        const currentIdx = neighborIds.indexOf(id);
        if (currentIdx !== -1) {
          setPrevConvId(currentIdx > 0 ? neighborIds[currentIdx - 1] : null);
          setNextConvId(currentIdx < neighborIds.length - 1 ? neighborIds[currentIdx + 1] : null);
        } else {
          console.warn("Current conversation ID not found in fetched neighbor context.");
        }
      } catch (error) {
        console.error("Error fetching neighbors:", error);
        toast.error("Could not load neighboring conversations.");
      } finally {
        setNeighborsLoading(false);
      }
    };
    if (id && currentListParams) {
        fetchNeighbors();
    } else {
        setPrevConvId(null);
        setNextConvId(null);
    }
  }, [id, currentListParams]);

  const togglePageContext = useCallback((messageId) => {
    setExpandedPageContexts(prev => ({ ...prev, [messageId]: !prev[messageId] }));
  }, []);

  const handleIncludeInternalPartsChange = (event) => {
    setIncludeInternalParts(event.target.checked);
  };

  const handleNavigateNeighbor = (neighborId) => {
    if (neighborId) {
      navigate(`/conversations/${neighborId}`, { state: { listParams: currentListParams } });
    }
  };

  if (loading && !conversation) { 
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 100px)' }}>
            <CircularProgress />
        </Box>
    );
  }

  const headerHeight = theme.mixins.toolbar.minHeight || 64;
  const pagePaddingYValue = 2; // Corresponds to theme.spacing(2)
  const pagePaddingY = theme.spacing(pagePaddingYValue);
  const availableHeight = `calc(100vh - ${headerHeight}px - (${pagePaddingY} * 2))`;

  return (
    <Grid container spacing={0} sx={{ height: '100%', flexWrap: 'nowrap', pt: pagePaddingYValue, pb: pagePaddingYValue }}>
      {/* Sidebar Grid Item */}
      <Grid item
        xs={0}
        sm={isSidebarOpen ? 4 : 0}
        md={isSidebarOpen ? 3 : 0}
        lg={isSidebarOpen ? 2.5 : 0}
        sx={{
          height: availableHeight,
          maxHeight: availableHeight,
          overflowY: 'auto',
          borderRight: { sm: isSidebarOpen ? (theme) => `1px solid ${theme.palette.divider}` : 'none' }, // Hide border when closed
          transition: theme.transitions.create(['width', 'margin', 'border'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          width: !isSidebarOpen && isSmUp ? '0px !important' : undefined,
          visibility: !isSidebarOpen && isSmUp && !isSidebarOpen ? 'hidden' : 'visible', // Prevent content flash
          display: { xs: 'none', sm: 'block' }, // Always manage with isSidebarOpen for smUp for transitions
        }}
      >
        {isSidebarOpen && (
            <ConversationSidebarList
            currentConversationId={id}
            initialListParams={currentListParams}
            />
        )}
      </Grid>

      {/* Main Content Area Grid Item */}
      <Grid item 
        xs={12}
        sm={isSidebarOpen ? 8 : 12}
        md={isSidebarOpen ? 9 : 12}
        lg={isSidebarOpen ? 9.5 : 12}
        sx={{ 
            height: availableHeight, 
            maxHeight: availableHeight,
            overflowY: 'auto', 
            p: {xs: 1.5, sm: 2, md: 2.5},
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create(['margin-left', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        }}>
        
        <Stack 
            direction={{xs: "column", sm: "row"}}
            justifyContent="space-between" 
            alignItems={{xs: "flex-start", sm: "center"}}
            spacing={{xs:1, sm:1}}
            sx={{ mb: 1.5, flexShrink: 0 }}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title={isSidebarOpen ? "Hide Conversations List" : "Show Conversations List"}>
                    <IconButton onClick={toggleSidebar} size="small" sx={{ display: { xs: 'inline-flex' }}}>
                        {isSidebarOpen ? <MenuOpenIcon /> : <ViewSidebarIcon />}
                    </IconButton>
                </Tooltip>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => currentListParams ? navigate(-1) : navigate('/conversations')}>
                    Back
                </Button>
            </Stack>
          
          {(prevConvId || nextConvId || neighborsLoading) && (
            <ButtonGroup variant="outlined" size="small" sx={{ alignSelf: {xs: 'flex-start', sm:'center'} }}>
              <Button onClick={() => handleNavigateNeighbor(prevConvId)} disabled={!prevConvId || neighborsLoading || loading} startIcon={<PrevIcon />}>
                Prev
              </Button>
              <Button onClick={() => handleNavigateNeighbor(nextConvId)} disabled={!nextConvId || neighborsLoading || loading} endIcon={<NextIcon />}>
                Next
              </Button>
            </ButtonGroup>
          )}
          {neighborsLoading && <CircularProgress size={24} sx={{ml:1, mr: 'auto', alignSelf: 'center'}}/>}

          {/* Corrected Switch with Tooltip */}
          <Tooltip title="Show/Hide internal system messages and raw tool outputs within the conversation flow.">
            {/* Wrap FormControlLabel in a Box or Stack for better alignment if needed, or ensure parent Stack handles alignment */}
            <FormControlLabel
                control={
                <Switch
                    checked={includeInternalParts}
                    onChange={handleIncludeInternalPartsChange}
                    icon={<VisibilityOffIcon fontSize="small" />} // Ensure icon size matches
                    checkedIcon={<VisibilityIcon fontSize="small" />}
                    size="small"
                />
                }
                label="Internal Parts"
                labelPlacement="start"
                sx={{ 
                    mr: 0, 
                    ml: {sm:'auto'}, // Pushes to right on sm+
                    // Add display flex and alignItems center to the FormControlLabel itself for better icon-label alignment
                    display: 'flex',
                    alignItems: 'center',
                    '& .MuiSwitch-root': { // Target the switch directly for margins if needed
                        // marginRight: theme.spacing(0.5) // Adjust space between switch and label
                    }
                }}
            />
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{display:'block', textAlign:'right', mb:2, flexShrink: 0}}>
            Conversation ID: {id}
        </Typography>

        <Paper sx={{ p: {xs: 1.5, sm:2, md:3}, mb: 3, flexShrink: 0 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Conversation Details
          </Typography>
          {loading && !conversation ? <CircularProgress sx={{display:'block', mx:'auto', my:2}}/> : conversation ? (
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                    <Typography variant="body1">
                    {conversation.created_at ? new Date(conversation.created_at).toLocaleString() : 'N/A'}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
                    <Typography variant="body1">
                    {conversation.updated_at ? new Date(conversation.updated_at).toLocaleString() : 'N/A'}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Agent Name</Typography>
                    <Typography variant="body1">{conversation.agent_name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Processed Messages</Typography>
                    <Typography variant="body1">{processedMessages.length}</Typography>
                </Grid>
            </Grid>
          ) : (
            <Typography sx={{py:2, textAlign:'center', color:'text.secondary'}}>Conversation data not available.</Typography>
          )}
          
          {systemPrompt && (
            <Box sx={{ mt: 3 }}>
              <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: alpha(theme.palette.info.main, 0.05)}}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                    <Typography variant="subtitle1" color="text.primary">System Prompt</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: alpha(theme.palette.info.main, 0.02), p: 1.5 }}>
                  <StyledSystemPromptPaper elevation={0}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                      {systemPrompt}
                    </Typography>
                  </StyledSystemPromptPaper>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Paper>

        <Typography variant="h6" sx={{ mb: 1.5, flexShrink: 0 }}>
          Conversation Messages
        </Typography>
        <Box sx={{ 
          p: {xs: 1, sm: 2}, 
          backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.5) : theme.palette.grey[100],
          borderRadius: theme.shape.borderRadius,
          flexGrow: 1, 
          overflowY: 'auto', 
          minHeight: '200px',
        }}>
          {loading && processedMessages.length === 0 && <CircularProgress sx={{display: 'block', margin: 'auto', mt: 4}}/>}
          {!loading && processedMessages.length === 0 && (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              {conversation ? 'No messages processed for this conversation.' : 'Loading messages or conversation not found.'}
            </Typography>
          )}
          {processedMessages.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {processedMessages.map((message) => {
                switch (message.type) {
                  case 'user': return <UserMessage key={message.id} {...message} />;
                  case 'assistant': return <AssistantMessage key={message.id} {...message} />;
                  case 'tool-call': return <ToolCallMessage key={message.id} {...message} />;
                  case 'tool-return': return <ToolReturnMessage key={message.id} {...message} />;
                  case 'page-context': return (
                    <PageContextDisplay key={message.id} messageId={message.id}
                      content={message.content} timestamp={message.timestamp}
                      isExpanded={!!expandedPageContexts[message.id]} onToggle={togglePageContext}
                    />);
                  default:
                    console.warn("Unknown message type:", message.type, message);
                    return <Box key={message.id || Math.random()} sx={{p:1, my:0.5, bgcolor: 'warning.light'}}>Unknown message type: {message.type}</Box>;
                }
              })}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};
  
export default ConversationDetail;